import React, { useEffect, useMemo, useState } from "react";
import { api, apiError, cleanPayload } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useI18n, formatDate } from "../i18n";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  Plus, Search, Users as UsersIcon, Phone, X, Eye, CalendarDays, Stethoscope, FileText, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const STATUS_CONFIG = {
  new: { tone: "info", labelKey: "patients.statusNew" },
  active: { tone: "success", labelKey: "patients.statusActive" },
  follow_up: { tone: "danger", labelKey: "patients.statusFollowUp" },
};

const FILTERS = ["all", "active", "new", "follow_up"];

export default function Patients() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const canConsult = ["owner", "doctor", "nurse"].includes(user?.role);
  const canInvoice = ["owner", "secretary", "cashier", "doctor"].includes(user?.role);
  const canSchedule = ["owner", "secretary", "doctor", "nurse"].includes(user?.role);

  function defaultForm() {
    return {
      first_name: "", last_name: "", phone: "", date_of_birth: "",
      gender: "", address: "", emergency_contact: "",
      blood_group: "", allergies: [], insurance: "",
    };
  }

  const load = async (search = "", status = "all") => {
    const params = {};
    if (search) params.search = search;
    if (status && status !== "all") params.status = status;
    const { data } = await api.get("/patients", { params });
    setItems(data);
  };

  useEffect(() => { load("", statusFilter); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const h = setTimeout(() => load(q, statusFilter), 250);
    return () => clearTimeout(h);
  }, [q, statusFilter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/patients", cleanPayload(form));
      toast.success(t("common.success"));
      setForm(defaultForm());
      setOpenNew(false);
      load(q, statusFilter);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  // Counts per status (computed client-side from current load — for filter chip badges)
  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, active: 0, follow_up: 0 };
    items.forEach((p) => { c[p.computed_status] = (c[p.computed_status] || 0) + 1; });
    return c;
  }, [items]);

  const input = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

  const goAction = (e, path) => {
    e.stopPropagation();
    nav(path);
  };

  return (
    <div>
      <PageHeader
        title={t("patients.title")}
        subtitle={t("patients.countShown").replace("{n}", items.length)}
        actions={
          <button onClick={() => setOpenNew(true)} data-testid="new-patient-btn"
            className="h-12 px-5 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center gap-2 transition-colors">
            <Plus className="w-5 h-5" /> {t("patients.new")}
          </button>
        }
      />

      {/* Search + Filters */}
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-faint" />
          <input
            placeholder={t("patients.searchPh")}
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="patient-search"
            className={`${input} pl-11`}
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => {
            const active = statusFilter === f;
            const count = counts[f] ?? 0;
            return (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                data-testid={`patient-filter-${f}`}
                className={`shrink-0 h-12 px-4 rounded-xl font-semibold text-sm flex items-center gap-2 transition-colors border
                  ${active
                    ? "bg-brand text-white border-brand"
                    : "bg-white text-ink-muted border-[var(--border-default)] hover:bg-subtle"}`}
              >
                <span>{t(`patients.filter${f === "all" ? "All" : f === "follow_up" ? "FollowUp" : f === "new" ? "New" : "Active"}`)}</span>
                <span className={`stat-number text-xs px-1.5 py-0.5 rounded-md ${active ? "bg-white/20" : "bg-subtle"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Card className="overflow-hidden">
        {items.length === 0 ? (
          <EmptyState icon={UsersIcon} title={t("common.noData")} />
        ) : (
          <div className="divide-y divide-[var(--border-default)]">
            {items.map((p) => {
              const cfg = STATUS_CONFIG[p.computed_status] || STATUS_CONFIG.new;
              return (
                <div
                  key={p.id}
                  onClick={() => nav(`/app/patients/${p.id}`)}
                  data-testid={`patient-row-${p.id}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 hover:bg-subtle/40 transition-colors cursor-pointer"
                  role="button"
                  tabIndex={0}
                >
                  {/* Avatar + identity */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-full bg-brand/10 text-brand flex items-center justify-center font-heading font-bold shrink-0">
                      {p.first_name?.[0]}{p.last_name?.[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-ink truncate">
                          {p.first_name} {p.last_name}
                        </span>
                        <Badge tone={cfg.tone}>{t(cfg.labelKey)}</Badge>
                        {p.unpaid_count > 0 && (
                          <Badge tone="warning" className="flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {p.unpaid_count} {t("patients.unpaidInvoices")}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-ink-muted mt-0.5 flex-wrap">
                        {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</span>}
                        {p.gender && <span>· {p.gender}</span>}
                        {p.date_of_birth && <span>· {formatDate(p.date_of_birth, lang)}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Visits stats */}
                  <div className="hidden md:block text-right shrink-0 mr-2">
                    <div className="stat-number font-bold text-ink text-base">
                      {p.visits_count} <span className="text-xs font-normal text-ink-muted">
                        {p.visits_count === 1 ? t("patients.visits") : t("patients.visits_plural")}
                      </span>
                    </div>
                    <div className="text-xs text-ink-muted">
                      {p.last_visit
                        ? `${t("patients.lastVisit")}: ${formatDate(p.last_visit, lang)}`
                        : t("patients.neverVisited")}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <button
                      onClick={(e) => goAction(e, `/app/patients/${p.id}`)}
                      data-testid={`patient-act-view-${p.id}`}
                      title={t("patients.actView")}
                      className="h-10 px-3 rounded-lg bg-subtle hover:bg-brand/10 hover:text-brand text-ink text-xs font-semibold flex items-center gap-1.5 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">{t("patients.actView")}</span>
                    </button>
                    {canSchedule && (
                      <button
                        onClick={(e) => goAction(e, `/app/appointments?patient_id=${p.id}`)}
                        data-testid={`patient-act-apt-${p.id}`}
                        title={t("patients.actAppointment")}
                        className="h-10 px-3 rounded-lg bg-[#2563eb]/10 text-[#1e40af] hover:bg-[#2563eb]/20 text-xs font-semibold flex items-center gap-1.5"
                      >
                        <CalendarDays className="w-4 h-4" />
                        <span className="hidden sm:inline">{t("patients.actAppointment")}</span>
                      </button>
                    )}
                    {canConsult && (
                      <button
                        onClick={(e) => goAction(e, `/app/consultations?patient_id=${p.id}`)}
                        data-testid={`patient-act-consult-${p.id}`}
                        title={t("patients.actConsult")}
                        className="h-10 px-3 rounded-lg bg-brand text-white hover:bg-brand-hover text-xs font-semibold flex items-center gap-1.5"
                      >
                        <Stethoscope className="w-4 h-4" />
                        <span className="hidden sm:inline">{t("patients.actConsult")}</span>
                      </button>
                    )}
                    {canInvoice && (
                      <button
                        onClick={(e) => goAction(e, `/app/invoices?patient_id=${p.id}`)}
                        data-testid={`patient-act-inv-${p.id}`}
                        title={t("patients.actInvoice")}
                        className="h-10 px-3 rounded-lg bg-terracotta/10 text-terracotta hover:bg-terracotta/20 text-xs font-semibold flex items-center gap-1.5"
                      >
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline">{t("patients.actInvoice")}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {openNew && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)]">
              <h3 className="font-heading font-bold text-xl text-ink">{t("patients.new")}</h3>
              <button onClick={() => setOpenNew(false)} className="p-2 rounded-lg hover:bg-subtle">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t("patients.firstName")} required>
                <input className={input} required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="p-first" />
              </Field>
              <Field label={t("patients.lastName")} required>
                <input className={input} required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="p-last" />
              </Field>
              <Field label={t("common.phone")}>
                <input className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="p-phone" />
              </Field>
              <Field label={t("patients.dob")}>
                <input type="date" className={input} value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="p-dob" />
              </Field>
              <Field label={t("patients.gender")}>
                <select className={input} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} data-testid="p-gender">
                  <option value="">—</option>
                  <option value="M">{t("patients.male")}</option>
                  <option value="F">{t("patients.female")}</option>
                  <option value="O">{t("patients.other")}</option>
                </select>
              </Field>
              <Field label={t("patients.bloodGroup")}>
                <input className={input} value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} placeholder="O+, A-, ..." />
              </Field>
              <Field label={t("patients.address")} className="sm:col-span-2">
                <input className={input} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>
              <Field label={t("patients.emergency")}>
                <input className={input} value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} />
              </Field>
              <Field label={t("patients.insurance")}>
                <input className={input} value={form.insurance} onChange={(e) => setForm({ ...form, insurance: e.target.value })} />
              </Field>
              <div className="sm:col-span-2 flex gap-3 pt-2">
                <button type="button" onClick={() => setOpenNew(false)} className="flex-1 h-12 rounded-xl border border-[var(--border-default)] font-semibold text-ink hover:bg-subtle">{t("common.cancel")}</button>
                <button type="submit" data-testid="p-save" className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold">
                  {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block mb-2 text-sm font-semibold text-ink">
        {label}{required && <span className="text-terracotta"> *</span>}
      </label>
      {children}
    </div>
  );
}
